import { EditableText, defineBlock } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { Container, Grid, Col } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@components/atoms/Button"
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import { format } from "date-fns"
import { createGradient } from "@utils/createGradient"
import { Heading } from "@components/atoms/Heading"
import { Chip } from "@components/atoms/Chip"
import { getQuarters } from "@utils/transportAffordabilityHelpers"
import { debounce, getAffordabilityColor } from "@utils/chartjsUtils"
import { useEvData, useRoadSafetyData, useTransportData } from "@hooks/queries"
import { VSpace } from "@components/layout/Spacer"
import { states } from "@components/atoms/data/Components"
import { extractQuarterlyData } from "@utils/evDataHelpers"

export const meta: BlockMeta = {
  title: "Data insight",
  category: "common",
  icon: "chart-pie",
  keywords: ["insight"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

ChartJS.register(...registerables)

export default defineBlock("general/data-insight", (props) => {
  const evIndexData = useEvData(undefined)
  const roadSafetyData = useRoadSafetyData(undefined)
  const transportAffordabilityData = useTransportData(undefined)
  const hydrating = useHydrating()

  const interactiveLink = useMemo(() => {
    if (props.interactiveData === "ev") return "/research-data/electric-vehicle/"
    if (props.interactiveData === "rs") return "/research-data/road-safety/"
    if (props.interactiveData === "ta") return "/research-data/transport-affordability/"
  }, [props])

  const rawData = useMemo(() => {
    if (props.interactiveData === "ev" && evIndexData.data?.evIndexData) {
      return evIndexData.data.evIndexData.map((data) => JSON.parse(data?.data ?? ""))
    }
    if (props.interactiveData === "rs" && roadSafetyData.data?.roadSafetyData) {
      return roadSafetyData.data.roadSafetyData
        .map((data) => JSON.parse(data?.data ?? ""))
        .sort((a, b) => {
          let [am, ay] = a["Date"].split("-")
          let [bm, by] = b["Date"].split("-")

          const dateA = new Date(`${am}-01-${ay}`)
          const dateB = new Date(`${bm}-01-${by}`)

          return dateA > dateB ? 1 : dateB > dateA ? -1 : 0
        })
    }
    if (props.interactiveData === "ta" && transportAffordabilityData.data?.transportAffordabilityData) {
      return transportAffordabilityData.data.transportAffordabilityData.map((data) => JSON.parse(data?.data ?? ""))
    }
    return null
  }, [
    roadSafetyData.data?.roadSafetyData,
    evIndexData.data?.evIndexData,
    transportAffordabilityData.data?.transportAffordabilityData,
  ])

  return (
    <InsightContainer>
      <div className="grid w-full grid-cols-12 gap-6">
        <Col initial={12} tablet={8} className={props.chartPosition ? "md:order-2" : "md:order-1"}>
          <Content>
            <div className="text">
              <EditableText as="h3" store="heading" placeholder="Enter heading..." />
              <EditableText as="p" store="content" placeholder="Enter content..." />
            </div>

            <ButtonGroup>
              <Button color="yellow" size="small" href={interactiveLink} icon="GRAPH" iconStyle="right">
                Interactive Data
              </Button>

              {props.readMoreLink && (
                <Button color="grey" size="small" href={props.readMoreLink.url ?? "#"}>
                  Read More
                </Button>
              )}
            </ButtonGroup>
          </Content>
        </Col>
        <Col initial={12} tablet={4} className={props.chartPosition ? "md:order-1" : "md:order-2"}>
          {!hydrating && rawData && (
            <ChartInstance>
              {props.interactiveData === "rs" && <RoadSafetyChart data={rawData} />}
              {props.interactiveData === "ta" && <TransportAffordabilityChart data={rawData} />}
              {props.interactiveData === "ev" && <EVIndexChart data={rawData} />}
            </ChartInstance>
          )}
        </Col>
      </div>
    </InsightContainer>
  )
})

const InsightContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container ref={ref} className={cn("my-3 border border-grey bg-white rounded-md p-4", className)} {...props} />
  ),
)
InsightContainer.displayName = "InsightContainer"

const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-light-white py-7 px-6 flex flex-col gap-4 w-full rounded-md justify-between h-full box-border",
      "[&_.text]:flex [&_.text]:flex-col [&_.text]:gap-4",
      "[&_h3]:type-title-m [&_h3]:my-0",
      "[&>p]:type-body-m [&>p]:my-0",
      className,
    )}
    {...props}
  />
))
Content.displayName = "Content"

const ButtonGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex gap-4 justify-self-end", className)} {...props} />
))
ButtonGroup.displayName = "ButtonGroup"

const ChartInstance = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full relative transition-all duration-[350ms] ease-movement rounded-md overflow-hidden flex flex-col pt-[100%]",
      "[&_canvas]:absolute [&_canvas]:bottom-2 [&_canvas]:left-2 [&_canvas]:right-2 [&_canvas]:!w-[calc(100%-(var(--spacing-2)*2))] [&_canvas]:!h-fit",
      "[&_header>div:last-of-type]:opacity-0 [&_header>div:last-of-type]:transition-all [&_header>div:last-of-type]:duration-[350ms] [&_header>div:last-of-type]:ease-movement",
      "hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)] [&:hover_header>div:last-of-type]:opacity-100",
      className,
    )}
    {...props}
  />
))
ChartInstance.displayName = "ChartInstance"

const RSSelections = {
  "National Actual": "National",
  "NSW Actual": "NSW",
  "VIC Actual": "VIC",
  "QLD Actual": "QLD",
  "SA Actual": "SA",
  "WA Actual": "WA",
  "TAS Actual": "TAS",
  "NT Actual": "NT",
  "ACT Actual": "ACT",
}

const RoadSafetyChart = ({ data }: { data: any }) => {
  const [currentSelection, setCurrentSelection] = useState<keyof typeof RSSelections>("National Actual")
  const chartRef = useRef<ChartJS>(null)
  const [currentData, setCurrentData] = useState<ChartData<"line">>({
    datasets: [],
  })

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 4 / 3,

    font: {
      size: 10,
    },

    scales: {
      y: {
        display: false,
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },

        border: {
          display: false,
        },

        ticks: {
          callback: (value, index, ticks) => {
            const label = currentData?.labels![index!] as string
            if (label) {
              let [m, y] = label.split("-")
              const date = new Date(`${m}-01-${y}`)

              if (index === 0) return

              // Check previous value
              const previousLabel = currentData?.labels![index! - 1] as string
              let [pm, py] = previousLabel.split("-")
              const previousDate = new Date(`${pm}-01-${py}`)

              if (format(date, "yyyy") !== format(previousDate, "yyyy")) return format(date, "yyyy")
            }
          },
        },
      },
    },

    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 4,
        caretSize: 0,
        caretPadding: 20,
        padding: 8,
        boxPadding: 5,
        usePointStyle: true,
        displayColors: false,

        callbacks: {
          title: () => "",
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
        },
      },

      legend: {
        display: false,
      },
    },
  }

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    setCurrentData({
      labels: data.map((row: any) => row["Date"]),
      datasets: [
        {
          data: data.map((row: any) => row[currentSelection]),
          borderColor: "#FEC32C",
          tension: 0.25,
          pointRadius: 0,
          pointHitRadius: 20,
          pointBorderWidth: 3,
          pointBackgroundColor: "#ffffff",
          backgroundColor: createGradient(
            chart.ctx,
            chart.chartArea,
            "rgba(254, 195, 44, 1)",
            chartRef.current?.height ? chartRef.current.height - 50 : 300,
            0.2,
          ),
          fill: "start",
        },
      ],
    })
  }, [data, currentSelection])

  const latestData = useMemo(() => {
    if (currentData.datasets[0]) {
      return currentData.datasets[0].data as number[]
    }
    return null
  }, [currentData])

  const calculateChange = useMemo(() => {
    if (currentData.datasets[0]) {
      const latest = currentData.datasets[0].data[currentData.datasets[0].data.length - 1] as number
      const prev = currentData.datasets[0].data[currentData.datasets[0].data.length - 13] as number

      return {
        value: latest - prev,
        direction: latest - prev > 0 ? "up" : latest - prev === 0 ? "none" : "down",
      }
    }
    return null
  }, [currentData])

  return (
    <>
      <ChartHeader>
        <EditableText as="label" store="rs-label" placeholder="Enter label..." />
        {latestData && <h2 className="!type-title-xxl">{latestData[latestData.length - 1]}</h2>}
        {calculateChange && (
          <Change>
            <Chip
              icon={
                calculateChange.direction === "up"
                  ? "ARROW_UP"
                  : calculateChange.direction === "none"
                    ? "PLACEHOLDER"
                    : "ARROW_DOWN"
              }
              iconStyle="left"
              color={
                calculateChange.direction === "up" ? "red" : calculateChange.direction === "none" ? "grey" : "green"
              }
            >
              {calculateChange.value.toString().replace("-", "")}
            </Chip>
            <span>vs last 12 months</span>
          </Change>
        )}

        <select onChange={(e) => setCurrentSelection(e.currentTarget.value as keyof typeof RSSelections)}>
          {Object.entries(RSSelections).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </select>
      </ChartHeader>
      <Chart ref={chartRef} type="line" data={currentData} options={options} />
    </>
  )
}

const TASelections = {
  "National City": {
    "City or Regional": "City",
    State: null,
  },
  "National Regional": {
    "City or Regional": "Regional",
    State: null,
  },
  "NSW City": {
    "City or Regional": "City",
    State: "NSW",
  },
  "NSW Regional": {
    "City or Regional": "Regional",
    State: "NSW",
  },
  "VIC City": {
    "City or Regional": "City",
    State: "VIC",
  },
  "VIC Regional": {
    "City or Regional": "Regional",
    State: "VIC",
  },
  "QLD City": {
    "City or Regional": "City",
    State: "QLD",
  },
  "QLD Regional": {
    "City or Regional": "Regional",
    State: "QLD",
  },
  "SA City": {
    "City or Regional": "City",
    State: "SA",
  },
  "SA Regional": {
    "City or Regional": "Regional",
    State: "SA",
  },
  "WA City": {
    "City or Regional": "City",
    State: "WA",
  },
  "WA Regional": {
    "City or Regional": "Regional",
    State: "WA",
  },
  "TAS City": {
    "City or Regional": "City",
    State: "TAS",
  },
  "TAS Regional": {
    "City or Regional": "Regional",
    State: "TAS",
  },
  "NT City": {
    "City or Regional": "City",
    State: "NT",
  },
  "NT Regional": {
    "City or Regional": "Regional",
    State: "NT",
  },
  ACT: {
    "City or Regional": "City",
    State: "ACT",
  },
}

const TransportAffordabilityChart = ({ data }: { data: any }) => {
  const hoverIndexRef = useRef<number | null>(null)

  const debouncedSetCurrentData = useRef(
    debounce((newData: ChartData<"doughnut">) => {
      setCurrentData(newData)
    }, 100),
  ).current

  const [currentSelection, setCurrentSelection] = useState<keyof typeof TASelections>("National City")
  const chartRef = useRef<ChartJS<"doughnut">>(null)
  const [currentData, setCurrentData] = useState<ChartData<"doughnut">>({
    datasets: [],
  })
  const [calculateChange, setCalculateChange] = useState<{
    value: number
    direction: "up" | "none" | "down"
    since: string
  } | null>(null)

  const [total, setTotal] = useState<number | null>(null)
  const [totalText, setTotalText] = useState<string | null>(null)

  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 4 / 3,
    cutout: "60%",

    animation: {
      onProgress: ChartDraw,
      onComplete: ChartDraw,
    },

    onHover(event, elements, chart) {
      if (elements.length > 0) {
        const hoverIndex = elements[0].index

        if (hoverIndexRef.current !== hoverIndex) {
          hoverIndexRef.current = hoverIndex

          setTotal(currentData.datasets[0].data[hoverIndex])
          if (currentData.labels) setTotalText(currentData.labels[hoverIndex] as string)

          const newData = {
            labels: currentData.labels,
            datasets: [
              {
                data: currentData.datasets[0].data,
                backgroundColor: [
                  getAffordabilityColor(7, false, hoverIndex === 0 ? false : true),
                  getAffordabilityColor(6, false, hoverIndex === 1 ? false : true),
                  getAffordabilityColor(5, false, hoverIndex === 2 ? false : true),
                  getAffordabilityColor(4, false, hoverIndex === 3 ? false : true),
                  getAffordabilityColor(3, false, hoverIndex === 4 ? false : true),
                  getAffordabilityColor(2, false, hoverIndex === 5 ? false : true),
                  getAffordabilityColor(1, false, hoverIndex === 6 ? false : true),
                  getAffordabilityColor(0, false, hoverIndex === 7 ? false : true),
                ],
                borderRadius: 6,
                borderWidth: 3,
              },
            ],
          }

          debouncedSetCurrentData(newData)
        }
      } else {
        if (hoverIndexRef.current !== null) {
          hoverIndexRef.current = null

          setTotal(null)
          setTotalText(null)

          const newData = {
            labels: currentData.labels,
            datasets: [
              {
                data: currentData.datasets[0].data,
                backgroundColor: [
                  getAffordabilityColor(7),
                  getAffordabilityColor(6),
                  getAffordabilityColor(5),
                  getAffordabilityColor(4),
                  getAffordabilityColor(3),
                  getAffordabilityColor(2),
                  getAffordabilityColor(1),
                  getAffordabilityColor(0),
                ],
                borderRadius: 6,
                borderWidth: 3,
              },
            ],
          }
          debouncedSetCurrentData(newData)
        }
      }
    },

    plugins: {
      tooltip: {
        enabled: false,
      },

      legend: {
        display: false,
      },
    },
  }

  function ChartDraw() {
    if (chartRef.current && currentData.datasets[0]) {
      var canvas = chartRef.current.canvas
      var ctx = canvas.getContext("2d")
      var cw = canvas.offsetWidth
      var ch = canvas.offsetHeight
      var cx = cw / 2
      var cy = ch / 2

      const sum = currentData.datasets[0].data.reduce((b, a) => b + a, 0)

      if (ctx) {
        ctx.translate(cx + 5, cy - 7)
        ctx.font = "bold 32px Manrope"
        ctx.fillStyle = "#222222"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.letterSpacing = "0em"
        ctx.fillText(`$${total ? total.toFixed(2) : sum.toFixed(2)}`, 0, 0)
        ctx.translate(-(cx + 5), -(cy - 7))

        ctx.translate(cx + 5, cy + 20)
        ctx.font = "bold 10px Manrope"
        ctx.fillStyle = "#5E5E5E"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.letterSpacing = ".12em"
        ctx.fillText(`${totalText ? totalText.replace("Registration", "Rego").toUpperCase() : `ALL`}`, 0, 0)
        ctx.translate(-(cx + 5), -(cy + 20))
      }
    }
  }

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    const selectedData: any[] = []

    data.forEach((dataset: any) => {
      if (currentSelection === "National City" || currentSelection === "National Regional") {
        if (dataset["City or Regional"] === TASelections[currentSelection]["City or Regional"]) {
          selectedData.push(dataset)
        }
      } else {
        if (
          dataset.State === TASelections[currentSelection].State &&
          dataset["City or Regional"] === TASelections[currentSelection]["City or Regional"]
        )
          selectedData.push(dataset)
      }
    })

    const quarters = getQuarters(selectedData)

    const dataset: number[] = []
    const labels: string[] = []
    const previousDataset: number[] = []

    if (currentSelection === "National City" || currentSelection === "National Regional") {
      const division = currentSelection === "National City" ? 9 : 8
      const loanav =
        selectedData
          .filter((dataset) => dataset.Field === "Car loan Payments")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const regoav =
        selectedData
          .filter((dataset) => dataset.Field === "Registration, CTP & Licensing")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const insuav =
        selectedData
          .filter((dataset) => dataset.Field === "Insurance")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const servav =
        selectedData
          .filter((dataset) => dataset.Field === "Servicing & Tyres")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const fuelav =
        selectedData
          .filter((dataset) => dataset.Field === "Fuel")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const tranav =
        selectedData
          .filter((dataset) => dataset.Field === "Public Transport")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const tollav =
        selectedData
          .filter((dataset) => dataset.Field === "Tolls")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const asstav =
        selectedData
          .filter((dataset) => dataset.Field === "Roadside Assist")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 1]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division

      const prevloanav =
        selectedData
          .filter((dataset) => dataset.Field === "Car loan Payments")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const prevregoav =
        selectedData
          .filter((dataset) => dataset.Field === "Registration, CTP & Licensing")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const previnsuav =
        selectedData
          .filter((dataset) => dataset.Field === "Insurance")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const prevservav =
        selectedData
          .filter((dataset) => dataset.Field === "Servicing & Tyres")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const prevfuelav =
        selectedData
          .filter((dataset) => dataset.Field === "Fuel")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const prevtranav =
        selectedData
          .filter((dataset) => dataset.Field === "Public Transport")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const prevtollav =
        selectedData
          .filter((dataset) => dataset.Field === "Tolls")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division
      const prevasstav =
        selectedData
          .filter((dataset) => dataset.Field === "Roadside Assist")
          .map((dataset) => parseFloat(dataset[quarters[quarters.length - 2]].replace("$", "")))
          .reduce((b, a) => b + a, 0) / division

      dataset.push(loanav, regoav, insuav, servav, fuelav, tranav, tollav, asstav)
      previousDataset.push(
        prevloanav,
        prevregoav,
        previnsuav,
        prevservav,
        prevfuelav,
        prevtranav,
        prevtollav,
        prevasstav,
      )

      selectedData.forEach((data) => {
        if (data.Field !== "Share of Income") {
          if (!labels.includes(data.Field)) labels.push(data.Field)
        }
      })
    } else {
      selectedData.forEach((data) => {
        if (data.Field !== "Share of Income") {
          labels.push(data.Field)
          dataset.push(parseFloat(data[quarters[quarters.length - 1]].replace("$", "")))
          previousDataset.push(parseFloat(data[quarters[quarters.length - 2]].replace("$", "")))
        }
      })
    }

    setCurrentData({
      labels: labels,
      datasets: [
        {
          data: dataset,
          backgroundColor: [
            getAffordabilityColor(7),
            getAffordabilityColor(6),
            getAffordabilityColor(5),
            getAffordabilityColor(4),
            getAffordabilityColor(3),
            getAffordabilityColor(2),
            getAffordabilityColor(1),
            getAffordabilityColor(0),
          ],
          borderRadius: 6,
          borderWidth: 3,
        },
      ],
    })

    const latest = dataset.reduce((b, a) => b + a, 0) as number
    const prev = previousDataset.reduce((b, a) => b + a, 0) as number

    setCalculateChange({
      value: parseFloat((latest - prev).toFixed(2)),
      direction: latest - prev > 0 ? "up" : latest - prev === 0 ? "none" : "down",
      since: quarters[quarters.length - 2],
    })
  }, [data, currentSelection])

  return (
    <>
      <ChartHeader className="flex-row items-start justify-between">
        <EditableText as="label" store="rs-label" placeholder="Enter label..." />

        {calculateChange && (
          <Change className="!opacity-100 flex-wrap">
            <Chip
              icon={
                calculateChange.direction === "up"
                  ? "ARROW_UP"
                  : calculateChange.direction === "none"
                    ? "PLACEHOLDER"
                    : "ARROW_DOWN"
              }
              iconStyle="left"
              color={
                calculateChange.direction === "up" ? "red" : calculateChange.direction === "none" ? "grey" : "green"
              }
            >
              ${calculateChange.value.toString().replace("-", "")}
            </Chip>
            <span>since {calculateChange.since}</span>
          </Change>
        )}

        <select
          onChange={(e) => setCurrentSelection(e.currentTarget.value as keyof typeof TASelections)}
          style={{ position: "static" }}
        >
          {Object.entries(TASelections).map(([key, value]) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
      </ChartHeader>
      <Chart ref={chartRef} type="doughnut" data={currentData} options={options} />
    </>
  )
}

// `typography: "text-xl"` below refers to a typeVariants key that doesn't
// exist in theme.css.tsx (only h1-h5/text-l/m/s/xs/label-l/m/s/caps do) —
// the stitches `typography` util silently no-ops on an unknown key (it
// calls the "for...in" loop over `undefined`), so the only typography this
// h2 ever actually got was the explicit fontSize/fontWeight/letterSpacing
// below it. Ported 1:1: no `type-*` class, just the literal overrides.
const ChartHeader = forwardRef<HTMLElement, ComponentPropsWithoutRef<"header">>(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn(
      "absolute top-4 w-[calc(100%-(var(--spacing-4)*2))] left-4 flex flex-col gap-4 z-10 pointer-events-none",
      "[&_label]:type-label-l",
      "[&_h2]:text-[56px] [&_h2]:font-semibold [&_h2]:tracking-[-0.055em]",
      "[&_select]:pointer-events-auto [&_select]:absolute [&_select]:w-auto [&_select]:top-0 [&_select]:right-0 [&_select]:bg-light-grey [&_select]:rounded-[6px] [&_select]:border-none [&_select]:p-2 [&_select]:type-label-s [&_select]:border-r-[10px] [&_select]:border-r-transparent [&_select:focus]:outline-yellow",
      className,
    )}
    {...props}
  />
))
ChartHeader.displayName = "ChartHeader"

const Change = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex gap-2 items-center [&>span]:type-label-caps [&>span]:text-dark-grey", className)}
    {...props}
  />
))
Change.displayName = "Change"

const EVIndexChart = ({ data }: { data: any }) => {
  const barChartRef = useRef<ChartJS>(null)
  const [currentSelection, setCurrentSelection] = useState<keyof typeof states>("all")
  const [barChartData, setBarChartData] = useState<ChartData<"bar">>({
    datasets: [],
  })

  const quarters = useMemo(() => {
    if (data) {
      return extractQuarterlyData(data[0]).map((data) => data.quarter)
    }
    return []
  }, [data])

  const latestData = useMemo(() => {
    if (data && quarters) {
      if (currentSelection === "all") {
        return {
          ice: data.filter((data: any) => data["FUEL TYPE"] === "ICE"),
          ev: data.filter((data: any) => data["FUEL TYPE"] !== "ICE"),
          latestTotal: data.reduce((a: any, b: any) => a + b[quarters[quarters.length - 1]], 0),
          evTotal: data
            .filter((data: any) => data["FUEL TYPE"] !== "ICE")
            .reduce((a: any, b: any) => a + b[quarters[quarters.length - 1]], 0),
        }
      }
      return {
        ice: data
          .filter((data: any) => data["STATE"] === states[currentSelection])
          .filter((data: any) => data["FUEL TYPE"] === "ICE"),
        ev: data
          .filter((data: any) => data["STATE"] === states[currentSelection])
          .filter((data: any) => data["FUEL TYPE"] !== "ICE"),
        latestTotal: data
          .filter((data: any) => data["STATE"] === states[currentSelection])
          .reduce((a: any, b: any) => a + b[quarters[quarters.length - 1]], 0),
        evTotal: data
          .filter((data: any) => data["STATE"] === states[currentSelection])
          .filter((data: any) => data["FUEL TYPE"] !== "ICE")
          .reduce((a: any, b: any) => a + b[quarters[quarters.length - 1]], 0),
      }
    }
    return null
  }, [data, quarters, currentSelection])

  useEffect(() => {
    if (latestData) {
      const barData: ChartData<"bar"> = {
        labels: [
          quarters[quarters.length - 6],
          quarters[quarters.length - 5],
          quarters[quarters.length - 4],
          quarters[quarters.length - 3],
          quarters[quarters.length - 2],
          quarters[quarters.length - 1],
        ],
        datasets: [
          {
            label: "ICE",
            data: [
              latestData.ice.reduce((a: any, b: any) => a + b[quarters[quarters.length - 1]], 0),
              latestData.ice.reduce((a: any, b: any) => a + b[quarters[quarters.length - 2]], 0),
              latestData.ice.reduce((a: any, b: any) => a + b[quarters[quarters.length - 3]], 0),
              latestData.ice.reduce((a: any, b: any) => a + b[quarters[quarters.length - 4]], 0),
              latestData.ice.reduce((a: any, b: any) => a + b[quarters[quarters.length - 5]], 0),
              latestData.ice.reduce((a: any, b: any) => a + b[quarters[quarters.length - 6]], 0),
            ],
            backgroundColor: "#FFE8B6",
            borderColor: "#FFE8B6",
            borderRadius: 4,
          },
          {
            label: "EV",
            data: [
              latestData.ev.reduce((a: any, b: any) => a + b[quarters[quarters.length - 1]], 0),
              latestData.ev.reduce((a: any, b: any) => a + b[quarters[quarters.length - 2]], 0),
              latestData.ev.reduce((a: any, b: any) => a + b[quarters[quarters.length - 3]], 0),
              latestData.ev.reduce((a: any, b: any) => a + b[quarters[quarters.length - 4]], 0),
              latestData.ev.reduce((a: any, b: any) => a + b[quarters[quarters.length - 5]], 0),
              latestData.ev.reduce((a: any, b: any) => a + b[quarters[quarters.length - 6]], 0),
            ],
            backgroundColor: "#FEC32C",
            borderColor: "#FEC32C",
            borderRadius: 4,
          },
        ],
      }

      setBarChartData(barData)
    }
  }, [latestData])

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 4 / 2.5,

    font: {
      size: 10,
    },

    scales: {
      y: {
        display: false,
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },

        border: {
          display: false,
        },
      },
    },

    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 4,
        caretSize: 0,
        caretPadding: 20,
        padding: 8,
        boxPadding: 5,
        usePointStyle: true,
        displayColors: false,

        callbacks: {
          title: () => "",
          labelColor: (ctx) => {
            return {
              borderColor: ctx.element.options.borderColor,
              backgroundColor: ctx.element.options.borderColor,
            }
          },
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
        },
      },

      legend: {
        display: false,
      },
    },
  }

  return (
    <>
      <ChartHeader>
        <EditableText as="label" store="rs-label" placeholder="Enter label..." />

        {latestData && (
          <h2 className="!type-title-xxl">{((latestData.evTotal / latestData.latestTotal) * 100).toFixed(2)}%</h2>
        )}

        <select onChange={(e) => setCurrentSelection(e.currentTarget.value as keyof typeof states)}>
          {Object.entries(states).map(([key, value]) => (
            <option key={key} value={key}>
              {key === "all" ? "National" : key.toUpperCase()}
            </option>
          ))}
        </select>
      </ChartHeader>
      <Chart type="bar" ref={barChartRef} data={barChartData} options={options} />
    </>
  )
}
